import { ButtonBase } from '@material-ui/core';
import * as React from 'react';

import * as DOMPurify from 'dompurify';
import { Editor } from 'react-text-editor';

import { NoteAttachment } from 'shared/models/note';

import { fullDateTime, timeCalendar } from '../common/timestamp';
import { PersonName } from '../containers/person-name';
import { FileDownloadLink } from '../core-ui/file-download-link';
import { useAudioPlayer } from '../hooks/use-audio-player';
import { User } from './user';
import { UserAvatar } from './user-avatar';

interface NoteProps {
    content: string;
    format: string;
    createdBy: string;
    createdAt: number;
    notable: string;
    edited: boolean;
    context: JSX.Element;
    className: string;
    options: JSX.Element;
    actions?: JSX.Element;
    attachments: NoteAttachment[];
}

const avatarSize = 32;

const MarkdownDisplay: React.FC<{ value: string }> = (props) => {
    return <Editor value={props.value ?? ''} format="markdown" disabled={true} toolbar="none" />;
};

export const Note: React.FC<NoteProps> = (props) => {
    const { actions, format, content, createdAt, edited, context, options, className, attachments, createdBy } = props;
    const { play } = useAudioPlayer();

    const body =
        format === 'html' ? (
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />
        ) : format === 'markdown' ? (
            <MarkdownDisplay value={content} />
        ) : (
            <div className="note-body-plaintext">{content}</div>
        );

    const attachmentsList = attachments.map((a) => {
        const attachment = (
            <div className="note-attachment">
                <div className="list-centered-icon">
                    <i className="fas fa-file-alt attachment-icon" />
                    <span>{a.filename}</span>
                </div>
            </div>
        );
        if (/\.mp3$/i.test(a.key)) {
            const handlePlay = () => {
                const [entity, id] = props.notable.split('-');
                const name = entity === 'persons' ? <PersonName id={id} /> : a.filename;
                const title = (
                    <React.Fragment>
                        <div>
                            {name} <span style={{ whiteSpace: 'nowrap' }}>· {fullDateTime(createdAt)}</span>
                        </div>
                        {context}
                    </React.Fragment>
                );
                play({ path: a.key, filename: a.filename, title });
            };
            return (
                <ButtonBase key={a.key} className="file-download-link" onClick={handlePlay}>
                    {attachment}
                </ButtonBase>
            );
        }
        return (
            <FileDownloadLink key={a.key} path={a.key} filename={a.filename}>
                {attachment}
            </FileDownloadLink>
        );
    });
    const attachmentsSection =
        attachments.length > 0 ? <div className="note-attachments-section">{attachmentsList}</div> : null;

    return (
        <React.Fragment>
            <div className="note-header-bar">
                <div className="note-header flex-columns-container">
                    <UserAvatar id={createdBy} size={avatarSize} />
                    <div className="note-header-text flex-rows-container">
                        <div>
                            <span className="text600 title">
                                <User id={createdBy} />
                            </span>
                            <span className="secondary small2">
                                {timeCalendar(createdAt)} {edited ? '(edited)' : ''}
                            </span>
                        </div>
                        {context}
                    </div>
                </div>

                {options}
            </div>
            <div className={className}>{body}</div>
            {attachmentsSection}
            {actions}
        </React.Fragment>
    );
};
